import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import { useRights } from "@/lib/rights";
import { useWebSocket, usePoll, STATUS, fmtTime, missionLabel } from "@/lib/helpers";
import MissionDialog from "@/components/MissionDialog";
import { Abandon, useUnavailableConfirm } from "@/components/UnavailableConfirm";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, GripVertical, ListChecks } from "lucide-react";
import { requestsOf, stepOf, STEP_LABELS, STEP_CLS } from "@/lib/ride";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }

export default function CalendarPage() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const voc = useVocab();
  const manager = user.role !== "employee";
  // Lot T5-0 : la secrétaire du transport n'a pas la vue Équipe (seul endroit où l'on glisse une course vers un
  // chauffeur) ; elle garde les « + » (nouvelle course, sans chauffeur pour elle) et ouvre une course selon ses
  // droits. En conciergerie, tout gestionnaire a ce droit : rien ne change.
  const rights = useRights();
  const dispatch = manager && rights.dispatch;
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(() => new Date());
  const [fEmp, setFEmp] = useState(() => localStorage.getItem("cal_femp") || "all");
  const [fType, setFType] = useState(() => localStorage.getItem("cal_ftype") || "all");
  const [dialog, setDialog] = useState({ open: false, mission: null, start: null });
  const [dragId, setDragId] = useState(null);
  // Transport (lot T5b) : glisser une course sur un chauffeur indisponible demande « L'attribuer quand même ? ».
  // Chez M CARWASH, le serveur ne demande jamais cette confirmation : rien ne change.
  const { confirmDialog, withConfirm } = useUnavailableConfirm();

  useEffect(() => { localStorage.setItem("cal_femp", fEmp); }, [fEmp]);
  useEffect(() => { localStorage.setItem("cal_ftype", fType); }, [fType]);

  const load = useCallback(() => {
    api.get("/missions").then((r) => setMissions(r.data));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (user.role !== "employee") api.get("/users").then((r) => setEmployees(r.data.filter((u) => u.role === "employee"))).catch(() => {});
  }, [user.role]);
  useEffect(() => { load(); }, [load]);
  useWebSocket(() => load());
  usePoll(() => load());

  const visible = missions.filter((m) => (fEmp === "all" || (fEmp === "unassigned" ? !m.employee_id : (m.employee_id || "") === fEmp)) && (fType === "all" || m.type === fType));
  const sameDay = (m, d) => new Date(m.start).toDateString() === d.toDateString();
  const forDay = (d) => visible.filter((m) => sameDay(m, d)).sort((a, b) => a.start.localeCompare(b.start));

  const weekDays = () => { const s = startOfWeek(cursor); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; }); };
  const monthCells = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const s = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  };
  const shift = (n) => {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + n);
    else if (view === "month") d.setMonth(d.getMonth() + n);
    else d.setDate(d.getDate() + n * 7);
    setCursor(d);
  };
  const rangeLabel = () => {
    if (view === "day") return cursor.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
    if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    const w = weekDays();
    return `${w[0].toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} – ${w[6].toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}`;
  };
  const openNew = (d) => { const s = new Date(d); s.setHours(9, 0, 0, 0); setDialog({ open: true, mission: null, start: s.toISOString() }); };
  const nameOf = (id, arr, key = "name") => arr.find((x) => x.id === id)?.[key] || "—";

  const reassign = async (missionId, employeeId) => {
    const m = missions.find((x) => x.id === missionId);
    if (!m || m.employee_id === employeeId) return;
    try {
      await withConfirm((force) => api.patch(`/missions/${missionId}`, { type: m.type, type_detail: m.type_detail || "", client_id: m.client_id, vehicle_id: m.vehicle_id || "",
        location: m.location || "", start: m.start, end: m.end, employee_id: employeeId, price_ttc: m.price_ttc || 0, notes: m.notes || "" },
        force ? { params: { force_unavailable: true } } : undefined));
      toast.success(`${voc.Mission} réassignée`); load();
    } catch (e) {
      if (e instanceof Abandon) return; // « Retour » : la course reste à son chauffeur
      toast.error(apiError(e.response?.data?.detail) || `Conflit d'horaire : ${voc.employee} déjà occupé`);
    }
  };

  const Card = ({ m, draggable, showDay }) => (
    <div draggable={draggable} onDragStart={() => setDragId(m.id)} onDragEnd={() => setDragId(null)}
      onClick={() => manager && setDialog({ open: true, mission: m, start: null })}
      title={`${fmtTime(m.start)} ${nameOf(m.client_id, clients)} — ${missionLabel(m)}`}
      className={cn("w-full text-left px-2 py-1.5 text-xs border-l-2 transition-colors hover:bg-muted/50 cursor-pointer flex flex-col gap-0.5",
        // Client absent (transport, lot T4a) : rouge, comme sa pastille partout ailleurs (sinon vert « Terminé »).
        voc.business === "transport" && m.status === "completed" && stepOf(m) === "no_show" ? STEP_CLS.no_show : STATUS[m.status]?.cls,
        m.status === "cancelled" && "line-through opacity-60")}
      style={{ borderLeftColor: "currentColor" }} data-testid={`cal-mission-${m.id}`}>
      <div className="flex items-center gap-1">
        {draggable && <GripVertical className="h-3 w-3 opacity-40 shrink-0" />}
        <span className="font-medium truncate">{fmtTime(m.start)} {nameOf(m.client_id, clients)}</span>
        {/* Demandes spéciales (transport, lot T1b-2) : icône + nombre ; le détail est dans la liste des courses. */}
        {requestsOf(m).length > 0 && (
          <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-sky-700 dark:text-sky-300 shrink-0" title={`${requestsOf(m).length} ${requestsOf(m).length > 1 ? "demandes spéciales" : "demande spéciale"}`} data-testid={`cal-requests-${m.id}`}>
            <ListChecks className="h-3 w-3" aria-hidden="true" />{requestsOf(m).length}<span className="sr-only">{requestsOf(m).length > 1 ? " demandes spéciales" : " demande spéciale"}</span>
          </span>
        )}
      </div>
      <span className="text-[11px] text-muted-foreground truncate">{missionLabel(m)}{voc.business === "transport" && m.dropoff_location ? ` → ${m.dropoff_location}` : ""}</span>
      {/* Étape de la course (transport, lot T4a) : « Sur place », « Client absent »… (rien tant qu'elle est à faire). */}
      {voc.business === "transport" && m.status !== "cancelled" && stepOf(m) && (
        <span className="text-[10px] font-medium truncate" data-testid={`cal-step-${m.id}`}>{STEP_LABELS[stepOf(m)]}</span>
      )}
    </div>
  );

  const views = [["day", "Jour"], ["week", "Semaine"], ["month", "Mois"], ...(dispatch ? [["team", "Équipe"]] : [])];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Planning partagé</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Calendrier</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex border border-border">
            {views.map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} className={cn("px-3 py-1.5 text-sm transition-colors", view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} data-testid={`view-${v}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium min-w-40 text-center capitalize">{rangeLabel()}</span>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {manager && (
          <Select value={fEmp} onValueChange={setFEmp}>
            <SelectTrigger className="w-48" data-testid="filter-employee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{`Tous les ${voc.employees}`}</SelectItem>
              <SelectItem value="unassigned">Non assigné</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger className="w-48" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {(settings?.mission_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {(fEmp !== "all" || fType !== "all") && <Button variant="ghost" size="sm" onClick={() => { setFEmp("all"); setFType("all"); }} data-testid="filter-clear">Réinitialiser</Button>}
      </div>

      {view === "day" && (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="font-display font-bold capitalize">{cursor.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}</div>
            {manager && <Button size="sm" className="gap-1" onClick={() => openNew(cursor)} data-testid="cal-add-day"><Plus className="h-4 w-4" />{` ${voc.Mission}`}</Button>}
          </div>
          <div className="p-3 space-y-2">
            {forDay(cursor).length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">{`Aucune ${voc.mission} ce jour.`}</p>}
            {forDay(cursor).map((m) => <Card key={m.id} m={m} draggable={false} />)}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {weekDays().map((d, i) => {
            const isToday = d.toDateString() === new Date().toDateString();
            return (
              <div key={i} className="bg-card border border-border rounded-2xl shadow-card min-h-40 flex flex-col">
                <div className={cn("px-3 py-2 border-b border-border flex items-center justify-between", isToday && "bg-accent/10")}>
                  <div className="text-xs font-medium">{DAYS[i]} <span className="text-muted-foreground">{d.getDate()}</span></div>
                  {/* Zone de clic de 32 × 32 px (p-2) sans changer l'apparence ni la place occupée (-m-2). */}
                  {manager && <button onClick={() => openNew(d)} className="p-2 -m-2 text-muted-foreground hover:text-accent" title={`Nouvelle ${voc.mission} ce jour-là`} data-testid={`cal-add-${i}`}><Plus className="h-4 w-4" /></button>}
                </div>
                <div className="p-2 space-y-1.5 flex-1">{forDay(d).map((m) => <Card key={m.id} m={m} draggable={false} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {view === "month" && (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="grid grid-cols-7 border-b border-border bg-muted/40">
            {DAYS.map((d) => <div key={d} className="p-2 text-xs font-medium text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthCells().map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = d.toDateString() === new Date().toDateString();
              const dm = forDay(d);
              return (
                <div key={i} className={cn("min-h-24 border-b border-r border-border p-1.5 last:border-r-0", !inMonth && "bg-muted/30 text-muted-foreground", "cursor-pointer hover:bg-muted/20")}
                  onClick={() => manager && openNew(d)} data-testid={`month-cell-${i}`}>
                  <div className={cn("text-xs mb-1 inline-grid place-items-center h-5 w-5 rounded-full", isToday && "bg-accent text-accent-foreground font-bold")}>{d.getDate()}</div>
                  <div className="space-y-0.5">
                    {dm.slice(0, 3).map((m) => (
                      <div key={m.id} onClick={(e) => { e.stopPropagation(); manager && setDialog({ open: true, mission: m, start: null }); }}
                        title={`${fmtTime(m.start)} ${nameOf(m.client_id, clients)} — ${missionLabel(m)}`}
                        className={cn("text-[11px] px-1 py-0.5 truncate rounded-sm", STATUS[m.status]?.cls, m.status === "cancelled" && "line-through opacity-60")} data-testid={`cal-mission-${m.id}`}>{fmtTime(m.start)} {nameOf(m.client_id, clients)}</div>
                    ))}
                    {dm.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{dm.length - 3} autres</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "team" && dispatch && (
        <div className="bg-card border border-border rounded-2xl shadow-card overflow-x-auto no-scrollbar">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-8 border-b border-border bg-muted/40">
              <div className="p-3 text-xs uppercase tracking-overline text-muted-foreground">{voc.Employee}</div>
              {weekDays().map((d, i) => <div key={i} className="p-3 text-xs font-medium border-l border-border">{DAYS[i]} {d.getDate()}</div>)}
            </div>
            <p className="px-3 py-2 text-xs text-muted-foreground border-b border-border">Glissez une {voc.mission} vers un autre {voc.employee} pour la réassigner. Les conflits d'horaire sont bloqués.</p>
            {[...employees, { id: "", name: "Non assigné" }].map((emp) => (
              <div key={emp.id || "none"} className={cn("grid grid-cols-8 border-b border-border last:border-0", dragId && "hover:bg-accent/5")}
                onDragOver={(e) => e.preventDefault()} onDrop={() => reassign(dragId, emp.id)} data-testid={`lane-${emp.id || "none"}`}>
                <div className="p-3 flex items-center gap-2 text-sm font-medium">
                  <span className="h-8 w-8 rounded-full bg-accent/20 grid place-items-center text-xs uppercase">{emp.name[0]}</span>{emp.name}
                </div>
                {weekDays().map((d, i) => (
                  <div key={i} className="p-1.5 border-l border-border space-y-1 min-h-16">
                    {visible.filter((m) => (m.employee_id || "") === emp.id && sameDay(m, d)).map((m) => <Card key={m.id} m={m} draggable />)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmDialog}
      <MissionDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} mission={dialog.mission} defaultStart={dialog.start} onSaved={load} />
    </div>
  );
}
